import React from 'react';
import { useHistory } from 'react-router-dom';
import { I18n } from '@aws-amplify/core';
import { SearchOutlined } from '@ant-design/icons';
import { Button, Col, Collapse, Form, Row } from 'antd';

import { toFilterQuery, toSearchParams } from '@/views/Bera/_components/Filter/utils';
import {
	Company,
	CreatedAt,
	Cycle,
	Line,
	ReportName,
	RPN,
	Sector,
	TotalBERA,
	UpdatedAt,
	Workstation
} from '@/views/Bera/_components/Filter/Fields';
import {
	BERA_LIST_PATH,
	EMPTY_FILTER_FIELDS,
	FILTER_PANEL_CLOSED_KEY,
	FILTER_PANEL_KEY
} from '@/views/Bera/constants';

import type { BeraFilterFormFields } from '@/views/Bera/types';
import * as S from './styles';

const { Panel } = Collapse;
const { useForm, useWatch } = Form;

interface FilterProps {
	initialValues: BeraFilterFormFields;
	onFilter: () => void;
}

export function Filter({ initialValues, onFilter }: Readonly<FilterProps>): JSX.Element {
	const [form] = useForm<BeraFilterFormFields>();
	const history = useHistory();

	const hasInitialValues = Object.keys(initialValues).length > 0;

	const formValues = useWatch([], form);
	const fieldValues = Object.values(formValues ?? {});
	const filledFields = fieldValues.filter(Boolean);
	const isEmpty = filledFields.length === 0;

	const defaultActiveKey = hasInitialValues ? FILTER_PANEL_KEY : FILTER_PANEL_CLOSED_KEY;

	function handleClear() {
		form.setFieldsValue(EMPTY_FILTER_FIELDS);
		history.push({ pathname: BERA_LIST_PATH, search: '' });
		onFilter();
	}

	function handleFinish(values: BeraFilterFormFields) {
		const query = toFilterQuery(values);
		history.push({ pathname: BERA_LIST_PATH, search: toSearchParams(query) });
		onFilter();
	}

	return (
		<S.CollapseStyled defaultActiveKey={defaultActiveKey}>
			<Panel key={FILTER_PANEL_KEY} header={I18n.get('Filter')}>
				<Form
					form={form}
					layout="vertical"
					onFinish={handleFinish}
					initialValues={initialValues}
				>
					<Row gutter={8}>
						<Col sm={4}>
							<Company />
						</Col>
						<Col sm={4}>
							<Sector />
						</Col>
						<Col sm={4}>
							<Line />
						</Col>
						<Col sm={4}>
							<Workstation />
						</Col>
					</Row>
					<Row gutter={8}>
						<Col sm={4}>
							<Cycle />
						</Col>
						<Col sm={4}>
							<ReportName />
						</Col>
						<Col sm={4}>
							<CreatedAt />
						</Col>
						<Col sm={4}>
							<UpdatedAt />
						</Col>
					</Row>
					<Row gutter={8}>
						<Col sm={4}>
							<TotalBERA />
						</Col>
						<Col sm={4}>
							<RPN />
						</Col>
					</Row>
					<Row gutter={8} style={{ paddingTop: '1.25rem' }}>
						<Col sm={4}>
							<Button
								block
								type="primary"
								htmlType="submit"
								disabled={isEmpty}
								icon={<SearchOutlined />}
							>
								{I18n.get('Search')}
							</Button>
						</Col>
						<Col sm={4}>
							<Button block onClick={handleClear}>
								{I18n.get('Clear')}
							</Button>
						</Col>
					</Row>
				</Form>
			</Panel>
		</S.CollapseStyled>
	);
}
